/**
 * Approve step: the patient's monthly check-in asked to change their dose.
 * Dr. Hinds answers it, and the answer drives the medication or directions
 * this renewal is prescribed with (the modal's own selection), never a
 * separate prescribing path.
 *
 * Tiered products (a clinician program's strength tiers): increase to the
 * next tier, change to any other strength, keep, or a visit first.
 * Products without tiers (sermorelin-type): the dose is the directions, so
 * keep or change directions, capped at one container per 28 days.
 */
import type { ReactNode } from "react";

import type {
  CheckinDoseRequest,
  DirectionTemplateOption,
  DoseRequestReasonCode,
  MedicationOption,
} from "@/types/clinician";

export type DoseAnswer =
  | "increase"
  | "change"
  | "change_directions"
  | "keep"
  | "visit"
  | "";

export const DOSE_REASONS: { code: DoseRequestReasonCode; label: string }[] = [
  { code: "under_4_weeks_at_dose", label: "Less than 4 weeks at the current dose" },
  { code: "side_effects", label: "Side effects reported" },
  { code: "responding_at_dose", label: "Responding as expected at this dose" },
  { code: "labs_needed", label: "Labs needed before changing the dose" },
  { code: "other", label: "Other (write below)" },
];

/** The strengths a dose answer can choose: the next tier and every other one. */
export function doseChoices(
  options: MedicationOption[],
  currentProductId: string | null | undefined,
): { tiered: boolean; next: MedicationOption | null; others: MedicationOption[] } {
  const tiers = options
    .filter((option) => option.tier_index != null)
    .sort((a, b) => (a.tier_index ?? 0) - (b.tier_index ?? 0));
  if (tiers.length === 0) return { tiered: false, next: null, others: [] };
  const current = tiers.find((option) => option.product_id === currentProductId);
  const others = tiers.filter((option) => option.product_id !== currentProductId);
  const next = current
    ? (tiers.find((option) => (option.tier_index ?? 0) > (current.tier_index ?? 0)) ?? null)
    : null;
  return { tiered: true, next, others };
}

export function doseAnswerIncomplete(
  answer: DoseAnswer,
  reason: DoseRequestReasonCode | "",
  note: string,
  choice: string,
): boolean {
  if (!answer) return true;
  const needsReason = answer === "keep" || answer === "visit";
  if (needsReason && !reason) return true;
  if (!needsReason && !choice) return true;
  return needsReason && reason === "other" && !note.trim();
}

function Option({
  value,
  answer,
  label,
  hint,
  disabled,
  onAnswer,
  children,
}: {
  value: DoseAnswer;
  answer: DoseAnswer;
  label: string;
  hint: string;
  disabled?: boolean;
  onAnswer: (value: DoseAnswer) => void;
  children?: ReactNode;
}) {
  return (
    <div
      className={`border rounded px-10 py-8 flex flex-col gap-6 ${
        answer === value ? "border-ink" : "border-line"
      } ${disabled ? "opacity-50" : ""}`}
    >
      <label className="flex items-start gap-10 cursor-pointer">
        <input
          type="radio"
          name="checkin-dose-answer"
          value={value}
          checked={answer === value}
          disabled={disabled}
          onChange={() => onAnswer(value)}
          className="mt-3"
        />
        <span className="flex flex-col">
          <span className="type-label-md">{label}</span>
          <span className="type-body-sm text-ink-muted">{hint}</span>
        </span>
      </label>
      {answer === value && children}
    </div>
  );
}

export function CheckinDoseRequestFields({
  request,
  options,
  templates,
  containerCapMl,
  answer,
  choice,
  reason,
  note,
  onAnswer,
  onChooseProduct,
  onChooseTemplate,
  onKeepCurrent,
  onReason,
  onNote,
}: {
  request: CheckinDoseRequest;
  options: MedicationOption[];
  /** Direction templates of the current product (products without tiers). */
  templates: DirectionTemplateOption[];
  containerCapMl: number | null | undefined;
  answer: DoseAnswer;
  /** Chosen product id (tiers) or direction template id (no tiers). */
  choice: string;
  reason: DoseRequestReasonCode | "";
  note: string;
  onAnswer: (value: DoseAnswer) => void;
  onChooseProduct: (productId: string) => void;
  onChooseTemplate: (template: DirectionTemplateOption) => void;
  /** Keep or a visit: the current product and directions, unchanged. */
  onKeepCurrent: () => void;
  onReason: (value: DoseRequestReasonCode | "") => void;
  onNote: (value: string) => void;
}) {
  const asked =
    request.asked_for === "discuss_dose"
      ? "would like to discuss their dose"
      : `asked to ${request.asked_for === "increase_dose" ? "increase" : "decrease"} their dose`;
  const { tiered, next, others } = doseChoices(options, request.current_product_id);
  const current = request.current_dose ?? "the current dose";
  const choose = (value: DoseAnswer) => {
    onAnswer(value);
    if (value === "increase" && next) onChooseProduct(next.product_id);
    if (value === "keep" || value === "visit") onKeepCurrent();
  };
  return (
    <fieldset className="border border-line rounded p-12 flex flex-col gap-10">
      <legend className="type-label-md font-semibold px-4">
        The patient {asked}
      </legend>
      <p className="type-body-sm text-ink-muted">
        {request.form_name ?? "Monthly check-in"}
        {request.checkin_at ? ` · ${new Date(request.checkin_at).toLocaleDateString()}` : ""}.
        Current dose {current}. Your answer is shown to support on the patient chart.
      </p>
      <div className="flex flex-col gap-6">
        {tiered ? (
          <>
            <Option
              value="increase"
              answer={answer}
              label="Increase to next dose"
              hint={next ? `${next.label} · this renewal ships the new strength` : "Already at the highest strength"}
              disabled={!next}
              onAnswer={choose}
            />
            <Option
              value="change"
              answer={answer}
              label="Change to a different dose"
              hint="Any available strength, higher or lower"
              disabled={others.length === 0}
              onAnswer={choose}
            >
              <label className="flex flex-col gap-4">
                <span className="type-label-sm font-semibold">Dose</span>
                <select
                  value={answer === "change" ? choice : ""}
                  onChange={(event) => onChooseProduct(event.target.value)}
                  className="border border-line rounded px-8 py-6 bg-page type-body-sm"
                >
                  <option value="">Choose a strength</option>
                  {others.map((option) => (
                    <option key={option.product_id} value={option.product_id}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </label>
            </Option>
            <Option
              value="keep"
              answer={answer}
              label="Keep current dose"
              hint={`${current} · a reason is required`}
              onAnswer={choose}
            />
          </>
        ) : (
          <>
            <Option
              value="keep"
              answer={answer}
              label="Keep current directions"
              hint="A reason is required"
              onAnswer={choose}
            />
            <Option
              value="change_directions"
              answer={answer}
              label="Change directions"
              hint={
                containerCapMl
                  ? `Units and schedule; up to ${containerCapMl} mL per 28 days (one container)`
                  : "Units and schedule"
              }
              disabled={templates.length === 0}
              onAnswer={choose}
            >
              <label className="flex flex-col gap-4">
                <span className="type-label-sm font-semibold">Directions</span>
                <select
                  value={answer === "change_directions" ? choice : ""}
                  onChange={(event) => {
                    const picked = templates.find((item) => item.id === event.target.value);
                    if (picked) onChooseTemplate(picked);
                  }}
                  className="border border-line rounded px-8 py-6 bg-page type-body-sm"
                >
                  <option value="">Choose directions</option>
                  {templates.map((template) => (
                    <option key={template.id} value={template.id} disabled={template.exceeds_container}>
                      {template.name}
                      {template.exceeds_container ? " — exceeds one vial per 28 days" : ""}
                    </option>
                  ))}
                </select>
              </label>
            </Option>
          </>
        )}
        <Option
          value="visit"
          answer={answer}
          label="Discuss at a visit first"
          hint="Requests a visit instead of approving now. A reason is required."
          onAnswer={choose}
        />
      </div>
      {(answer === "keep" || answer === "visit") && (
        <label className="flex flex-col gap-4">
          <span className="type-label-sm font-semibold">Reason</span>
          <select
            value={reason}
            onChange={(event) => onReason(event.target.value as DoseRequestReasonCode | "")}
            className="border border-line rounded px-8 py-6 bg-page type-body-sm"
          >
            <option value="">Choose a reason</option>
            {DOSE_REASONS.map((item) => (
              <option key={item.code} value={item.code}>
                {item.label}
              </option>
            ))}
          </select>
        </label>
      )}
      {answer !== "" && (
        <label className="flex flex-col gap-4">
          <span className="type-label-sm font-semibold">
            Note for the care team{reason === "other" ? "" : " (optional)"}
          </span>
          <textarea
            rows={2}
            value={note}
            onChange={(event) => onNote(event.target.value)}
            className="border border-line rounded px-8 py-6 bg-page type-body-sm"
          />
        </label>
      )}
    </fieldset>
  );
}
